import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Download as DownloadIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { offersService } from "@/services/offers.service";
import { ActionKey, canPerformAction } from "@/auth/actionMatrix";
import { canAccessPage, PageKey } from "@/auth/accessMatrix";
import { RootState } from "@/store";

type Metrics = {
  min_limit: string | number | null;
  max_limit: string | number | null;
  min_total_rate: string | number | null;
  max_total_rate: string | number | null;
};
type Token = {
  id: string;
  token: string;
  direction: "IN" | "OUT";
  currency: string | null;
};
type Offer = {
  id: string;
  payment_method_group_id: string;
  name: string;
  description: string | null;
  tokens: Token[];
  metrics: Metrics;
};
type Method = { id: string; name: string; tokens: string[] };
const fmt = (value: string | number | null, suffix = "") =>
  value == null
    ? "—"
    : `${Number(value).toLocaleString("ru-RU", { maximumFractionDigits: 2 })}${suffix}`;

export default function Offers() {
  const navigate = useNavigate();
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const canManageOffers = canPerformAction(currentUser, ActionKey.OFFERS_UPDATE);
  const canViewFinance = canAccessPage(currentUser, PageKey.FINANCE);
  const [items, setItems] = useState<Offer[]>([]);
  const [methods, setMethods] = useState<Method[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<Offer | null>(null);
  const [methodId, setMethodId] = useState("");
  const [description, setDescription] = useState("");
  const [search, setSearch] = useState("");
  const [direction, setDirection] = useState("");
  const [currency, setCurrency] = useState("");
  const currencies = Array.from(new Set(items.flatMap((offer) => offer.tokens.map((token) => token.currency).filter(Boolean)))) as string[];
  const filteredItems = items.filter((offer) => {
    const needle = search.trim().toLowerCase();
    return (!needle || offer.name.toLowerCase().includes(needle) || (offer.description ?? "").toLowerCase().includes(needle) || offer.tokens.some((token) => token.token.toLowerCase().includes(needle))) && (!direction || offer.tokens.some((token) => token.direction === direction)) && (!currency || offer.tokens.some((token) => token.currency === currency));
  });
  const load = async () => {
    setLoading(true);
    try {
      const data = await offersService.list();
      setItems(data.items ?? []);
      setMethods(data.available_methods ?? []);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    void load();
  }, []);
  const beginCreate = () => {
    setEditing(null);
    setMethodId(methods[0]?.id ?? "");
    setDescription("");
    setOpen(true);
  };
  const beginEdit = (offer: Offer) => {
    setEditing(offer);
    setMethodId(offer.payment_method_group_id);
    setDescription(offer.description ?? "");
    setOpen(true);
  };
  const save = async () => {
    if (!editing && !methodId) return;
    setSaving(true);
    try {
      editing
        ? await offersService.update(editing.id, description)
        : await offersService.create(methodId, description);
      setOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!editing || !confirm(`Удалить оффер «${editing.name}»?`)) return;
    setSaving(true);
    try {
      await offersService.remove(editing.id);
      setOpen(false);
      await load();
    } finally {
      setSaving(false);
    }
  };
  const goToMethod = () => {
    if (editing)
      navigate(
        `/finance?tab=method-map&method=${encodeURIComponent(editing.payment_method_group_id)}`,
      );
  };
  if (loading && !items.length)
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress />
      </Box>
    );
  return (
    <Stack spacing={3}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        gap={2}
        flexWrap="wrap"
      >
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Офферы
          </Typography>
          <Typography color="text.secondary">
            Коммерческие описания напрямую связаны с методами и их включёнными
            токенами.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => void offersService.export("xlsx")}
          >
            Выгрузить XLSX
          </Button>
          {canManageOffers && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={!methods.length}
              onClick={beginCreate}
            >
              Создать оффер
            </Button>
          )}
        </Stack>
      </Box>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
          <TextField fullWidth label="Поиск по офферу, методу или токену" value={search} onChange={(event) => setSearch(event.target.value)} />
          <FormControl sx={{ minWidth: 180 }}><InputLabel>Направление</InputLabel><Select value={direction} label="Направление" onChange={(event) => setDirection(event.target.value)}><MenuItem value="">Все</MenuItem><MenuItem value="IN">IN</MenuItem><MenuItem value="OUT">OUT</MenuItem></Select></FormControl>
          <FormControl sx={{ minWidth: 180 }}><InputLabel>Валюта</InputLabel><Select value={currency} label="Валюта" onChange={(event) => setCurrency(event.target.value)}><MenuItem value="">Все</MenuItem>{currencies.map((item) => <MenuItem key={item} value={item}>{item}</MenuItem>)}</Select></FormControl>
          <Button onClick={() => { setSearch(""); setDirection(""); setCurrency(""); }}>Сбросить</Button>
        </Stack>
      </Paper>
      <Box
        display="grid"
        gridTemplateColumns={{ xs: "1fr", lg: "repeat(2, minmax(0, 1fr))" }}
        gap={2}
      >
        {filteredItems.map((offer) => (
          <Paper
            key={offer.id}
            variant="outlined"
            role={canManageOffers ? "button" : undefined}
            tabIndex={canManageOffers ? 0 : undefined}
            onClick={() => canManageOffers && beginEdit(offer)}
            onKeyDown={(event) => {
              if (canManageOffers && (event.key === "Enter" || event.key === " ")) {
                event.preventDefault();
                beginEdit(offer);
              }
            }}
            sx={{
              p: 2.5,
              cursor: canManageOffers ? "pointer" : "default",
              transition: "border-color .15s, background-color .15s",
              "&:hover, &:focus-visible": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
                outline: "none",
              },
            }}
          >
            <Typography variant="h6" fontWeight={700}>
              {offer.name}
            </Typography>
            <Stack
              direction="row"
              spacing={0.75}
              mt={1}
              flexWrap="wrap"
              useFlexGap
            >
              {offer.tokens.map((token) => (
                <Chip
                  key={token.id}
                  size="small"
                  label={`${token.direction}: ${token.token}`}
                  color={token.direction === "IN" ? "primary" : "success"}
                  variant="outlined"
                />
              ))}
            </Stack>
            <Typography sx={{ whiteSpace: "pre-wrap", mt: 2, minHeight: 72 }}>
              {offer.description || "Описание пока не добавлено"}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              mt={2}
              mb={1}
            >
              Актуальные данные трейдеров
            </Typography>
            <Box
              display="grid"
              gridTemplateColumns="repeat(2, minmax(0, 1fr))"
              gap={1}
            >
              <Box sx={{ bgcolor: "action.hover", p: 1.25, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Лимиты трейдеров
                </Typography>
                <Typography fontWeight={700}>
                  {fmt(offer.metrics.min_limit)} –{" "}
                  {fmt(offer.metrics.max_limit)}
                </Typography>
              </Box>
              <Box sx={{ bgcolor: "action.hover", p: 1.25, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  Общая ставка трейдеров
                </Typography>
                <Typography fontWeight={700}>
                  {fmt(offer.metrics.min_total_rate, "%")} –{" "}
                  {fmt(offer.metrics.max_total_rate, "%")}
                </Typography>
              </Box>
            </Box>
          </Paper>
        ))}
      </Box>
      {!filteredItems.length && (
        <Paper variant="outlined" sx={{ p: 5, textAlign: "center" }}>
          <Typography color="text.secondary">
            Офферов пока нет. Создайте первый оффер из готового метода.
          </Typography>
        </Paper>
      )}
      <Dialog
        open={open}
        onClose={() => !saving && setOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editing ? "Редактирование оффера" : "Новый оффер"}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <FormControl fullWidth disabled={Boolean(editing)}>
              <InputLabel>Метод</InputLabel>
              <Select
                value={methodId}
                label="Метод"
                onChange={(event) => setMethodId(event.target.value)}
              >
                {editing && (
                  <MenuItem value={editing.payment_method_group_id}>
                    {editing.name}
                  </MenuItem>
                )}
                {methods.map((method) => (
                  <MenuItem key={method.id} value={method.id}>
                    {method.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Название"
              value={
                editing?.name ??
                methods.find((method) => method.id === methodId)?.name ??
                ""
              }
              disabled
              helperText="Название берётся из связанного метода и не изменяется в оффере"
            />
            <TextField
              label="Описание оффера"
              multiline
              minRows={8}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              inputProps={{ maxLength: 4000 }}
              helperText={`${description.length}/4000`}
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "space-between", px: 3, pb: 2 }}>
          <Box>
            {editing && canManageOffers && (
              <Button
                color="error"
                startIcon={<DeleteIcon />}
                disabled={saving}
                onClick={() => void remove()}
              >
                Удалить
              </Button>
            )}
          </Box>
          <Stack direction="row" spacing={1}>
            {editing && canViewFinance && (
              <Button
                startIcon={<OpenInNewIcon />}
                disabled={saving}
                onClick={goToMethod}
              >
                Перейти к карте метода
              </Button>
            )}
            <Button onClick={() => setOpen(false)} disabled={saving}>
              Отмена
            </Button>
            <Button
              variant="contained"
              disabled={saving || (!editing && !methodId)}
              onClick={() => void save()}
            >
              {saving ? "Сохранение…" : "Сохранить"}
            </Button>
          </Stack>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
